import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { RESOURCES } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { RecordForm } from "@/components/RecordForm";
import { Button } from "@/components/ui/button";

// Generic list + create/edit page driven by RESOURCES config in lib/modules.js
export default function ResourceModule() {
  const { module } = useParams();
  const cfg = RESOURCES[module];
  const { org } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [patients, setPatients] = useState({});
  const [action, setAction] = useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [module],
    queryFn: async () => (await api.get(cfg.endpoint)).data || [],
    enabled: !!cfg,
  });

  useQuery({
    queryKey: ["patients-map"],
    queryFn: async () => {
      const list = (await api.get("/patients")).data || [];
      const map = {};
      list.forEach((p) => (map[p.id] = p.full_name));
      setPatients(map);
      return map;
    },
    enabled: !!cfg && cfg.columns.some((c) => c.ref === "patient"),
  });

  const mutation = useMutation({
    mutationFn: async (payload) => {
      if (payload.patient_id && patients[payload.patient_id]) payload = { ...payload, patient_name: patients[payload.patient_id] };
      if (editing?.id && cfg.canEdit) return (await api.put(`${cfg.endpoint}/${editing.id}`, payload)).data;
      return (await api.post(cfg.endpoint, payload)).data;
    },
    onSuccess: () => {
      toast.success(`${cfg.singular} saved`);
      qc.invalidateQueries({ queryKey: [module] });
      setOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const actionMut = useMutation({
    mutationFn: async ({ act, row, payload }) => (await api.put(act.path(row), payload || {})).data,
    onSuccess: (_, { act }) => {
      toast.success(`${act.label} — done`);
      qc.invalidateQueries({ queryKey: [module] });
      setAction(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const runAction = (act, row) => (act.fields ? setAction({ act, row }) : actionMut.mutate({ act, row }));

  if (!cfg) return <div className="p-6 text-slate-500">Unknown module.</div>;

  const Icon = cfg.icon;
  const baseCols = cfg.columns.map((c) =>
    c.ref === "patient"
      ? { ...c, render: (row) => patients[row.patient_id] || row.patient_name || row.patient_id || "—" }
      : c
  );
  const columns = cfg.actions ? [...baseCols, {
    key: "_actions", label: "",
    render: (row) => (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {cfg.actions.filter((a) => a.show(row)).map((a) => (
          <Button key={a.key} size="sm" variant="outline" data-testid={`action-${a.key}-${row.id}`} className="h-7 border-teal-200 text-teal-700" onClick={() => runAction(a, row)}>{a.label}</Button>
        ))}
      </div>
    ),
  }] : baseCols;

  return (
    <PaywallWrapper>
      <PageHeader title={cfg.title} subtitle={`${rows.length} ${rows.length === 1 ? "record" : "records"}`} testId={`page-${module}`}>
        {!cfg.readonly && (
          <Button data-testid={`add-${module}`} className="gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus className="h-4 w-4" /> New {cfg.singular}
          </Button>
        )}
      </PageHeader>

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        currency={org?.currency || "NGN"}
        onRowClick={cfg.canEdit ? (row) => { setEditing(row); setOpen(true); } : undefined}
        emptyTitle={`No ${cfg.title.toLowerCase()} yet`}
        emptyDescription={cfg.readonly ? "Records will appear here as they are generated." : `Create your first ${cfg.singular.toLowerCase()} to get started.`}
        emptyAction={!cfg.readonly ? `New ${cfg.singular}` : undefined}
        onEmptyAction={() => { setEditing(null); setOpen(true); }}
        testId={`table-${module}`}
      />

      {!cfg.readonly && (
        <RecordForm
          open={open}
          onOpenChange={setOpen}
          title={`${editing?.id ? "Edit" : "New"} ${cfg.singular}`}
          fields={cfg.fields}
          initial={editing}
          submitting={mutation.isPending}
          onSubmit={(p) => mutation.mutate(p)}
        />
      )}
      {action && (
        <RecordForm
          open={!!action}
          onOpenChange={(o) => !o && setAction(null)}
          title={action.act.label}
          fields={action.act.fields}
          initial={null}
          submitting={actionMut.isPending}
          onSubmit={(payload) => actionMut.mutate({ act: action.act, row: action.row, payload })}
        />
      )}
    </PaywallWrapper>
  );
}

// Lazy import to avoid circular dependency weight
import { PaywallGate } from "@/components/PaywallGate";
function PaywallWrapper({ children }) {
  return <PaywallGate>{children}</PaywallGate>;
}
